import { playSound } from '../../utils/appUtils';
import { supabase } from '../../supabase';
// KnowledgeCards.js — MM2-style collectible knowledge cards with rarity system
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── RARITY CONFIG ────────────────────────────────────────
export const RARITIES = {
  common:    { label:'Common',    color:'#9E9E9E', bg:'#F5F5F5', glow:'#9E9E9E44', shimmer:false, dropRate:0.60, emoji:'⚪' },
  rare:      { label:'Rare',      color:'#1565C0', bg:'#E3F2FD', glow:'#1565C066', shimmer:false, dropRate:0.25, emoji:'🔵' },
  epic:      { label:'Epic',      color:'#7B1FA2', bg:'#F3E5F5', glow:'#7B1FA266', shimmer:true,  dropRate:0.10, emoji:'🟣' },
  legendary: { label:'Legendary', color:'#E65100', bg:'#FFF3E0', glow:'#FF6B0066', shimmer:true,  dropRate:0.04, emoji:'🟠' },
  godly:     { label:'GODLY',     color:'#FFD600', bg:'linear-gradient(135deg,#FFD600,#FF4500)', glow:'#FFD60088', shimmer:true, dropRate:0.01, emoji:'🌟' },
};

// ─── CARD FACTS LIBRARY ───────────────────────────────────
const FACTS = {
  maths: [
    { id:'m1', fact:'Zero is the only number that cannot be represented in Roman numerals.', rarity:'common' },
    { id:'m2', fact:'A "googol" is 1 followed by 100 zeros. Google named itself after this number!', rarity:'rare' },
    { id:'m3', fact:'If you shuffle a deck of 52 cards, the order has almost certainly never existed before in history.', rarity:'epic' },
    { id:'m4', fact:'The number Pi (π) has been calculated to over 100 trillion digits. It never repeats.', rarity:'legendary' },
    { id:'m5', fact:'In a room of just 23 people, there\'s a 50% chance two people share a birthday. With 70 people, it\'s 99.9%.', rarity:'epic' },
    { id:'m6', fact:'Mathematicians proved there are different sizes of infinity — some infinities are bigger than others!', rarity:'godly' },
    { id:'m7', fact:'The equals sign (=) was invented in 1557 by Welsh mathematician Robert Recorde.', rarity:'common' },
    { id:'m8', fact:'1 + 2 + 3 + 4 + ... to infinity = -1/12. This strange result is actually used in physics!', rarity:'legendary' },
  ],
  science: [
    { id:'s1', fact:'Your body has more bacteria cells than human cells — about 38 trillion vs 30 trillion!', rarity:'common' },
    { id:'s2', fact:'A teaspoon of neutron star material weighs about 1 billion tonnes.', rarity:'rare' },
    { id:'s3', fact:'Hot water freezes faster than cold water. This is called the Mpemba Effect — nobody fully knows why.', rarity:'epic' },
    { id:'s4', fact:'DNA in a single human cell, if stretched out, would be 2 metres long. Your body has 37 trillion cells.', rarity:'legendary' },
    { id:'s5', fact:'Octopuses have three hearts, blue blood, and can edit their own RNA — essentially rewriting their genetic code.', rarity:'epic' },
    { id:'s6', fact:'Sound travels 4x faster through water than air, and 15x faster through steel.', rarity:'common' },
    { id:'s7', fact:'The human brain generates enough electricity to power a small light bulb continuously.', rarity:'rare' },
    { id:'s8', fact:'A single bolt of lightning contains enough energy to toast 100,000 slices of bread.', rarity:'legendary' },
    { id:'s9', fact:'Honey never spoils. Edible 3,000-year-old honey has been found in Egyptian tombs.', rarity:'common' },
    { id:'s10',fact:'Trees communicate with each other through underground fungi networks, sharing nutrients and warning signals.', rarity:'godly' },
  ],
  olympiad: [
    { id:'o1', fact:'The International Mathematical Olympiad has been held every year since 1959 — except 1980.', rarity:'common' },
    { id:'o2', fact:'India won its first IMO gold medal in 2024 after 30 years of participation.', rarity:'rare' },
    { id:'o3', fact:'Terence Tao won the IMO gold medal at age 13 — the youngest ever. He became a professor at 24.', rarity:'epic' },
    { id:'o4', fact:'The Science Olympiad Foundation runs the world\'s largest school Olympiad with 6 crore students annually.', rarity:'common' },
    { id:'o5', fact:'Maryam Mirzakhani was the first woman to win the Fields Medal (mathematics Nobel) — at age 37.', rarity:'legendary' },
    { id:'o6', fact:'Ramanujan taught himself mathematics from a single textbook and discovered thousands of new theorems.', rarity:'godly' },
  ],
  india: [
    { id:'i1', fact:'India invented the number zero, the decimal system, and algebra. Without these, modern computers cannot exist.', rarity:'rare' },
    { id:'i2', fact:'India\'s Chandrayaan-3 landed on the Moon\'s south pole for the first time in history — at a fraction of NASA\'s cost.', rarity:'epic' },
    { id:'i3', fact:'The game of Chess was invented in India around 600 CE. It was called Chaturanga.', rarity:'common' },
    { id:'i4', fact:'India processes 46% of all real-time digital payments in the world — more than the USA, UK, and China combined.', rarity:'legendary' },
    { id:'i5', fact:'The first university in the world, Nalanda (Bihar), had 10,000 students and 2,000 teachers — in 400 CE.', rarity:'epic' },
    { id:'i6', fact:'India\'s ISRO built a Mars mission (Mangalyaan) for ₹450 crore — less than the Hollywood film Gravity.', rarity:'legendary' },
    { id:'i7', fact:'The Bhadla Solar Park in Rajasthan is the world\'s largest solar farm — the size of Mumbai city.', rarity:'rare' },
    { id:'i8', fact:'Yoga was practiced in India 5,000 years ago. Today 300 million people worldwide practice it.', rarity:'common' },
    { id:'i9', fact:'India has the world\'s largest film industry by number of films produced — Bollywood + 20 regional industries.', rarity:'common' },
  ],
  tech: [
    { id:'t1', fact:'The first computer bug was an actual bug — a moth found in a Navy computer in 1947 by Grace Hopper.', rarity:'common' },
    { id:'t2', fact:'Google processes 8.5 billion searches every day — 99,000 per second.', rarity:'rare' },
    { id:'t3', fact:'The first iPhone (2007) had less computing power than your microwave oven today.', rarity:'common' },
    { id:'t4', fact:'If you stored all data created in 2024 on DVDs and stacked them, the pile would reach to the Moon and back 66 times.', rarity:'epic' },
    { id:'t5', fact:'The internet weighs about 50 grams — that\'s the weight of all electrons in motion carrying internet data.', rarity:'legendary' },
    { id:'t6', fact:'An AI trained on chess for 4 hours (AlphaZero) became the best chess player in history, defeating 100-year-old strategies.', rarity:'godly' },
    { id:'t7', fact:'More people in India have access to a mobile phone than a toilet — showing how fast technology spreads.', rarity:'rare' },
  ],
  general: [
    { id:'g1', fact:'Cleopatra lived closer in time to the Moon landing than to the building of the Great Pyramids.', rarity:'epic' },
    { id:'g2', fact:'Oxford University is older than the Aztec Empire. It was founded in 1096.', rarity:'rare' },
    { id:'g3', fact:'The shortest war in history lasted 38-45 minutes — the Anglo-Zanzibar War in 1896.', rarity:'common' },
    { id:'g4', fact:'A day on Venus is longer than a year on Venus — it rotates so slowly it completes an orbit before one rotation.', rarity:'legendary' },
    { id:'g5', fact:'There are more stars in the universe than grains of sand on all of Earth\'s beaches combined.', rarity:'epic' },
    { id:'g6', fact:'Wombats produce cube-shaped droppings — they are the only animals in the world to do this.', rarity:'common' },
    { id:'g7', fact:'If the Sun were the size of a white blood cell, the Milky Way galaxy would be the size of the USA.', rarity:'legendary' },
  ],
};

// ─── ALL FACTS FLATTENED ──────────────────────────────────
const ALL_FACTS = Object.entries(FACTS).flatMap(([subject, facts]) =>
  facts.map(f => ({ ...f, subject }))
);

// ─── DROP LOGIC ───────────────────────────────────────────
export function rollCardDrop(subject = 'general') {
  // 40% chance of dropping any card on a correct answer
  if (Math.random() > 0.40) return null;

  const roll = Math.random();
  let rarity;
  if      (roll < 0.01) rarity = 'godly';
  else if (roll < 0.05) rarity = 'legendary';
  else if (roll < 0.15) rarity = 'epic';
  else if (roll < 0.40) rarity = 'rare';
  else                  rarity = 'common';

  // Pick a fact matching subject or general
  const pool = ALL_FACTS.filter(f =>
    (f.subject === subject || f.subject === 'general') && f.rarity === rarity
  );
  const fallbackPool = ALL_FACTS.filter(f => f.rarity === rarity);
  const chosen = (pool.length > 0 ? pool : fallbackPool)[Math.floor(Math.random() * (pool.length || fallbackPool.length))];
  if (!chosen) return null;

  return {
    ...chosen,
    rarity,
    earnedAt: Date.now(),
    uid: `${chosen.id}_${Date.now()}`,
  };
}

// ─── CARD STORAGE — localStorage + Supabase sync ──────────
export function getCards() {
  try { return JSON.parse(localStorage.getItem('ns_knowledge_cards') || '[]'); }
  catch { return []; }
}

export function saveCard(card) {
  const cards = getCards();
  cards.unshift(card);
  if (cards.length > 200) cards.splice(200);
  localStorage.setItem('ns_knowledge_cards', JSON.stringify(cards));
  // Sync to Supabase in background
  supabase.auth.getSession().then(({ data: { session } }) => {
    const email = session?.user?.email;
    if (email) {
      supabase.from('bm_child_profiles')
        .update({ knowledge_cards: cards })
        .eq('parent_email', email)
        .then(() => {}).catch(() => {});
    }
  }).catch(() => {});
  return cards;
}

// Restore cards from Supabase on login (call once after profile loads)
export async function restoreCardsFromDB(email) {
  if (!email) return;
  const local = getCards();
  if (local.length > 0) return; // already have local cards
  const { data } = await supabase.from('bm_child_profiles')
    .select('knowledge_cards').eq('parent_email', email).single();
  if (data?.knowledge_cards?.length) {
    localStorage.setItem('ns_knowledge_cards', JSON.stringify(data.knowledge_cards));
  }
}
export function getCardStats() {
  const cards = getCards();
  const counts = { common:0, rare:0, epic:0, legendary:0, godly:0 };
  cards.forEach(c => { if (counts[c.rarity] !== undefined) counts[c.rarity]++; });
  return { total: cards.length, counts };
}

// ─── CARD DROP POPUP ──────────────────────────────────────
export function CardDropPopup({ card, onClose }) {
  if (!card) return null;
  // Play sound based on rarity
  React.useEffect(() => {
    playSound(card.rarity === 'godly' || card.rarity === 'legendary' ? 'levelup' : 'pet_card');
  }, []);
  const r = RARITIES[card.rarity];
  const isGodly = card.rarity === 'godly';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity:0 }}
        animate={{ opacity:1 }}
        exit={{ opacity:0 }}
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:9999,
          display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
        onClick={onClose}>

        {/* Glow burst */}
        <motion.div initial={{ scale:0 }} animate={{ scale:[0,3,0] }}
          transition={{ duration:0.8, ease:'easeOut' }}
          style={{ position:'absolute', width:200, height:200, borderRadius:'50%',
            background:r.glow, filter:'blur(40px)', pointerEvents:'none' }}/>

        <motion.div
          initial={{ scale:0, rotate:-15, y:60 }}
          animate={{ scale:1, rotate:0, y:0 }}
          exit={{ scale:0, y:-60 }}
          transition={{ type:'spring', stiffness:280, damping:22 }}
          onClick={e => e.stopPropagation()}
          style={{ width:'100%', maxWidth:320, borderRadius:24,
            background: isGodly ? 'linear-gradient(135deg,#FFD600,#FF6B00,#FF2D55,#8B2BE2,#0066FF)' : 'white',
            padding: isGodly ? 3 : 0,
            boxShadow:`0 20px 60px ${r.glow}` }}>

          <div style={{ background:'white', borderRadius:isGodly?22:24, overflow:'hidden' }}>
            {/* Header */}
            <div style={{ background:isGodly?'linear-gradient(135deg,#FFD600,#FF6B00)':r.color,
              padding:'20px 20px 16px', textAlign:'center' }}>
              {isGodly && (
                <motion.div animate={{ rotate:[0,5,-5,0] }} transition={{ duration:0.5, repeat:Infinity }}>
                  <span style={{ fontSize:14, fontWeight:900, color:'white', letterSpacing:4 }}>✦ GODLY DROP ✦</span>
                </motion.div>
              )}
              <p style={{ margin:isGodly?'6px 0 0':0, fontSize:13, fontWeight:900,
                color:'rgba(255,255,255,0.85)', letterSpacing:2, fontFamily:"'Nunito',sans-serif" }}>
                {r.emoji} {r.label.toUpperCase()} CARD
              </p>
            </div>

            {/* Subject badge */}
            <div style={{ textAlign:'center', padding:'16px 20px 0' }}>
              <span style={{ background:r.color+'22', color:r.color, borderRadius:20,
                padding:'4px 14px', fontSize:11, fontWeight:900, fontFamily:"'Nunito',sans-serif" }}>
                {card.subject?.toUpperCase()}
              </span>
            </div>

            {/* Fact */}
            <div style={{ padding:'14px 22px 20px' }}>
              <p style={{ margin:0, fontSize:15, fontWeight:700, color:'#1A1A2E',
                lineHeight:1.6, textAlign:'center', fontFamily:"'Nunito',sans-serif" }}>
                💡 {card.fact}
              </p>
            </div>

            {/* Shimmer for rare+ */}
            {r.shimmer && (
              <div style={{ height:3, background:`linear-gradient(90deg,transparent,${r.color},transparent)` }}/>
            )}

            {/* CTA */}
            <div style={{ padding:'14px 20px 20px', textAlign:'center' }}>
              <motion.button whileTap={{ scale:0.95 }} onClick={onClose}
                style={{ background:isGodly?'linear-gradient(135deg,#FFD600,#FF6B00)':r.color,
                  border:'none', borderRadius:14, padding:'12px 32px',
                  fontSize:14, fontWeight:900, color:'white', cursor:'pointer',
                  fontFamily:"'Nunito',sans-serif",
                  boxShadow:`0 4px 16px ${r.glow}` }}>
                ✨ Add to Collection!
              </motion.button>
              <p style={{ margin:'8px 0 0', fontSize:11, color:'#AAA',
                fontWeight:700, fontFamily:"'Nunito',sans-serif" }}>
                Tap anywhere to continue
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── CARD ITEM (for collection view) ─────────────────────
function CardItem({ card, size='normal' }) {
  const r = RARITIES[card.rarity];
  const small = size === 'small';

  return (
    <motion.div whileHover={{ y:-4, scale:1.02 }} whileTap={{ scale:0.96 }}
      style={{ borderRadius:small?12:16, overflow:'hidden', cursor:'pointer',
        boxShadow:`0 4px 16px ${r.glow}`,
        border:`2px solid ${r.color}44` }}>
      {/* Top bar */}
      <div style={{ background:r.color, padding:small?'6px 10px':'8px 14px',
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:small?9:10, fontWeight:900, color:'white',
          letterSpacing:1, fontFamily:"'Nunito',sans-serif" }}>
          {r.emoji} {r.label.toUpperCase()}
        </span>
        <span style={{ fontSize:small?9:10, fontWeight:700, color:'rgba(255,255,255,0.8)',
          fontFamily:"'Nunito',sans-serif" }}>
          {card.subject?.toUpperCase()}
        </span>
      </div>
      {/* Body */}
      <div style={{ background:card.rarity==='godly'?'linear-gradient(135deg,#FFF8E1,#FFF3E0)':'white',
        padding:small?'8px 10px':'12px 14px' }}>
        <p style={{ margin:0, fontSize:small?10:12, fontWeight:700, color:'#1A1A2E',
          lineHeight:1.5, fontFamily:"'Nunito',sans-serif" }}>
          💡 {card.fact}
        </p>
      </div>
      {r.shimmer && <div style={{ height:2, background:`linear-gradient(90deg,transparent,${r.color},transparent)` }}/>}
    </motion.div>
  );
}

// ─── FULL COLLECTION SCREEN ───────────────────────────────
export function CardCollection({ onBack }) {
  const [cards, setCards] = useState(getCards);
  const [filter, setFilter] = useState('all');
  const stats = getCardStats();

  const filtered = filter === 'all' ? cards : cards.filter(c => c.rarity === filter);

  return (
    <div style={{ minHeight:'100vh', background:'#0D0D2E', fontFamily:"'Nunito',sans-serif", paddingBottom:80 }}>
      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#1A0040,#0D0D2E)', padding:'16px 16px 20px',
        borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
        <button onClick={onBack} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)',
          fontSize:14, fontWeight:800, cursor:'pointer', padding:0, fontFamily:"'Nunito',sans-serif", marginBottom:10 }}>
          ← Back
        </button>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h2 style={{ margin:0, fontSize:22, fontWeight:900, color:'white' }}>🃏 Knowledge Cards</h2>
            <p style={{ margin:'2px 0 0', fontSize:12, color:'rgba(255,255,255,0.6)', fontWeight:700 }}>
              {stats.total} cards collected
            </p>
          </div>
          <div style={{ textAlign:'right' }}>
            {stats.counts.godly > 0 && (
              <div style={{ background:'linear-gradient(135deg,#FFD600,#FF6B00)', borderRadius:10,
                padding:'4px 12px', fontSize:12, fontWeight:900, color:'white' }}>
                🌟 {stats.counts.godly} GODLY
              </div>
            )}
          </div>
        </div>

        {/* Rarity counts */}
        <div style={{ display:'flex', gap:6, marginTop:14, overflowX:'auto', scrollbarWidth:'none' }}>
          {[['all','All',stats.total,'#888'],
            ...Object.entries(RARITIES).map(([key,r]) => [key, r.label, stats.counts[key], r.color])
          ].map(([key, label, count, color]) => (
            <motion.button key={key} whileTap={{ scale:0.94 }}
              onClick={() => setFilter(key)}
              style={{ flexShrink:0, padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer',
                background: filter===key ? color : 'rgba(255,255,255,0.08)',
                color: filter===key ? 'white' : 'rgba(255,255,255,0.5)',
                fontSize:11, fontWeight:900, fontFamily:"'Nunito',sans-serif" }}>
              {label} ({count})
            </motion.button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div style={{ textAlign:'center', padding:'60px 24px' }}>
          <p style={{ fontSize:48, margin:'0 0 12px' }}>🃏</p>
          <p style={{ fontSize:16, fontWeight:900, color:'rgba(255,255,255,0.5)', margin:0 }}>
            {filter === 'all' ? 'No cards yet! Answer quiz questions to earn cards.' : `No ${filter} cards yet. Keep answering questions!`}
          </p>
        </div>
      )}

      {/* Cards grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, padding:'16px' }}>
        {filtered.map(card => (
          <CardItem key={card.uid || card.id} card={card}/>
        ))}
      </div>
    </div>
  );
}

// ─── MINI CARD STRIP (for home screen / profile) ──────────
export function CardStrip({ onViewAll }) {
  const cards = getCards().slice(0, 6);
  const stats = getCardStats();
  if (stats.total === 0) return null;

  return (
    <div style={{ margin:'12px 16px 0' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <span style={{ fontSize:14, fontWeight:900, color:'#1A1A2E', fontFamily:"'Nunito',sans-serif" }}>
          🃏 My Cards <span style={{ fontSize:12, color:'#888' }}>({stats.total})</span>
        </span>
        <motion.button whileTap={{ scale:0.95 }} onClick={onViewAll}
          style={{ background:'none', border:'1.5px solid #E0E0E0', borderRadius:10,
            padding:'4px 12px', fontSize:11, fontWeight:900, color:'#555', cursor:'pointer',
            fontFamily:"'Nunito',sans-serif" }}>
          View All →
        </motion.button>
      </div>
      <div style={{ display:'flex', gap:8, overflowX:'auto', scrollbarWidth:'none', paddingBottom:4 }}>
        {cards.map(card => {
          const r = RARITIES[card.rarity];
          return (
            <motion.div key={card.uid||card.id} whileTap={{ scale:0.9 }}
              style={{ flexShrink:0, width:90, borderRadius:12, overflow:'hidden',
                border:`2px solid ${r.color}55`, boxShadow:`0 2px 8px ${r.glow}` }}>
              <div style={{ background:r.color, padding:'4px 6px', textAlign:'center' }}>
                <span style={{ fontSize:9, fontWeight:900, color:'white', letterSpacing:1 }}>{r.label.toUpperCase()}</span>
              </div>
              <div style={{ background:'white', padding:'6px 8px' }}>
                <p style={{ margin:0, fontSize:9, fontWeight:700, color:'#333', lineHeight:1.4,
                  display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                  {card.fact}
                </p>
              </div>
            </motion.div>
          );
        })}
        {stats.total > 6 && (
          <motion.div whileTap={{ scale:0.9 }} onClick={onViewAll}
            style={{ flexShrink:0, width:90, borderRadius:12, background:'#F5F5F5',
              display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
              border:'2px dashed #DDD', minHeight:80 }}>
            <p style={{ margin:0, fontSize:11, fontWeight:900, color:'#AAA', textAlign:'center' }}>
              +{stats.total - 6}<br/>more
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
